import React, { useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ORG_DB_NAMES, ORG_LABELS, TEAM_LABELS } from '../config';
import { useFetch } from '../hooks';
import { Alert, Button, Field, Input, Select } from '../ui/basics';
import { Drawer, useToast } from '../ui/overlay';
import { dayFromNow, rules, useFormValidation } from '../validation';
import NameSelect from './NameSelect';
import { docFile } from '../pages/onboarding/docs';

const BLANK = {
  candidate_name: '', name_in_timesheets: '', name_in_payroll_paychex: '', date_of_joining: '', client: '',
  employment_status: '', payment_terms: '', project_end: '', recruiter: '', loc: '', lead: '', pay_rate: '',
  bill_rate: '', actual_margin: '', gross_margin: '', source: '', active_inactive: 'Active', projected_end_date: '', override_reason: '',
};
const EMPLOYMENT = ['Employee', 'Contract', '1099', 'Child Support'];
const PAYMENT_TERMS = ['Weekly', 'Weekly 2nd', '1st Bi-Weekly', '2nd Bi-Weekly', 'Net-45'];
const money = (v) => Number(String(v ?? '').replace(/[$,\s]/g, ''));
const withCurrent = (list, value) => (value && !list.includes(value) ? [{ value, label: `${value} (legacy value)` }, ...list] : list);

/** Same rules as the server. New placements need both rates; an old placement is only re-checked where it is changed. */
const schemaFor = (editing, needsReason) => ({
  override_reason: rules.check((v) => !needsReason || String(v || '').trim().length >= 5, 'Give a reason of at least 5 characters.'),
  candidate_name: [rules.required('Candidate name'), rules.maxLength(100, 'Candidate name')],
  date_of_joining: [rules.required('Start date'), rules.date('Start date', { max: dayFromNow(366) })],
  client: rules.required('Client'),
  recruiter: rules.required('Recruiter'),
  employment_status: rules.required('Employment status'),
  payment_terms: rules.required('Payment terms'),
  lead: rules.required('Team'),
  loc: rules.maxLength(50, 'Location'),
  source: rules.maxLength(50, 'Source'),
  bill_rate: [...(editing ? [] : [rules.required('Bill rate')]), rules.number('Bill rate', { min: 0, max: 1000, above: 0 })],
  pay_rate: [...(editing ? [] : [rules.required('Pay rate')]), rules.number('Pay rate', { min: 0, max: 1000, above: 0 })],
  gross_margin: [rules.number('Gross margin', { min: -1000, max: 1000 }), rules.check((v, all) => {
    const [b, p, g] = [money(all.bill_rate), money(all.pay_rate), money(v)];
    return !String(v ?? '').trim() || ![b, p, g].every(Number.isFinite) || !String(all.bill_rate).trim() || !String(all.pay_rate).trim() || Math.abs(b - p - g) <= 0.05;
  }, 'Gross margin should be bill rate minus pay rate.')],
  actual_margin: rules.number('Actual margin', { min: -1000, max: 1000 }),
  project_end: [rules.date('End date', { max: dayFromNow(0) }), rules.notBefore('date_of_joining', 'End date', 'the start date')],
  projected_end_date: [rules.date('Projected end date'), rules.notBefore('date_of_joining', 'Projected end date', 'the start date')],
});

/**
 * Add or edit a placement. A new placement is made by converting a candidate offer (`offer`): its candidate, client and
 * organization are fixed. An administrator can instead add one with no offer (`override`), giving a reason.
 */
export default function PlacementForm({ placement, offer, override, defaultOrg, onClose, onSaved }) {
  const { orgs, teams, allTeams } = useAuth();
  const toast = useToast();
  const editing = Boolean(placement);
  const writable = orgs.filter((o) => o !== 'combined');
  const [orgKey, setOrgKey] = useState(() => {
    if (editing || offer) return Object.keys(ORG_DB_NAMES).find((k) => ORG_DB_NAMES[k] === (editing ? placement : offer).organization) || writable[0];
    return writable.includes(defaultOrg) ? defaultOrg : writable[0];
  });
  const [original] = useState(() => (editing ? { ...BLANK, ...Object.fromEntries(Object.entries(placement).map(([k, v]) => [k, v ?? ''])) } : null));
  const incomplete = Boolean(offer) && !((offer.required_documents || []).length > 0 && offer.required_documents.every((d) => docFile(d)));
  const needsReason = !editing && (Boolean(override) || incomplete);
  const [form, setForm] = useState(() => original || (offer
    ? { ...BLANK, candidate_name: offer.candidate_name || '', client: offer.client || '', date_of_joining: offer.expected_start_date || '', loc: offer.client_location || '' }
    : BLANK));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const lookups = useFetch('lookups/', { organization: orgKey });
  const teamKeys = allTeams ? Object.keys(TEAM_LABELS) : teams;

  const set = (key, value) => setForm((prev) => {
    const next = { ...prev, [key]: value };
    if (key === 'project_end') next.active_inactive = value ? 'Inactive' : 'Active';
    if (key === 'bill_rate' || key === 'pay_rate') {
      const bill = parseFloat(next.bill_rate), pay = parseFloat(next.pay_rate);
      if (Number.isFinite(bill) && Number.isFinite(pay)) next.gross_margin = (bill - pay).toFixed(2);
    }
    return next;
  });
  const v = useFormValidation(form, schemaFor(editing, needsReason), original);
  const bind = (key) => ({ value: form[key] ?? '', onChange: (e) => set(key, e.target.value), ...v.props(key) });
  const changeOrg = (key) => {
    setOrgKey(key);
    // Each organization has its own clients and recruiters, so a name picked for the old one must not carry over.
    if (!editing) setForm((prev) => ({ ...prev, client: '', recruiter: '' }));
  };

  const save = async () => {
    setError('');
    if (!v.check()) return;
    setBusy(true);
    const body = { ...form, organization: ORG_DB_NAMES[orgKey] };
    ['project_end', 'projected_end_date'].forEach((k) => { if (!body[k]) body[k] = null; });
    if (offer) body.offer_id = offer.onboard_id;
    if (!needsReason) delete body.override_reason;
    try {
      let res;
      if (editing) res = await api.patch(`placements/${placement.s_no}/`, body);
      else res = await api.post('placements/', body);
      toast(editing ? 'Placement updated' : offer ? 'Offer converted to a placement' : 'Placement added', 'success');
      onSaved(res);
    } catch (e) { setError(v.fail(e) ? 'The server found a problem with the highlighted fields.' : e.message); setBusy(false); }
  };

  const negative = Number.isFinite(money(form.bill_rate)) && Number.isFinite(money(form.pay_rate)) && String(form.bill_rate).trim() && String(form.pay_rate).trim() && money(form.pay_rate) > money(form.bill_rate);

  return (
    <Drawer title={editing ? `Edit placement · ${placement.candidate_name}` : offer ? `Convert to placement · ${offer.candidate_name}` : 'Add placement without an offer'} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={save}>{editing ? 'Save changes' : offer ? 'Convert to placement' : 'Add placement'}</Button></>}>
      <div className="form-grid">
        {(v.summary || error) && <div className="full"><Alert>{error || v.summary}</Alert></div>}
        {offer && <div className="full"><Alert tone="info">Candidate, client, start date and location come from the offer. Add the recruiter, team, rates and payment details.</Alert></div>}
        {needsReason && (
          <div className="full">
            <Alert tone="warning">{override
              ? 'This placement is not linked to an offer. Only administrators can do this, for example for historical entries. The reason is saved on the placement.'
              : 'This offer still has documents missing. Only administrators can convert it, and the reason is saved on the placement.'}</Alert>
          </div>
        )}
        {needsReason && <Field label="Reason *" className="full" error={v.shown('override_reason')}><Input maxLength={250} {...bind('override_reason')} /></Field>}
        <Field label="Organization" className="full">
          <Select value={orgKey} disabled={editing || Boolean(offer)} onChange={(e) => changeOrg(e.target.value)} options={writable.map((o) => ({ value: o, label: ORG_LABELS[o] }))} />
        </Field>
        <Field label="Candidate name *" error={v.shown('candidate_name')}><Input maxLength={120} readOnly={Boolean(offer)} {...bind('candidate_name')} /></Field>
        <Field label="Start date *" error={v.shown('date_of_joining')}><Input type="date" min="2000-01-01" max={dayFromNow(366)} {...bind('date_of_joining')} /></Field>
        <Field label="Name in timesheets"><Input {...bind('name_in_timesheets')} /></Field>
        <Field label="Name in payroll"><Input {...bind('name_in_payroll_paychex')} /></Field>
        <Field label="Client *" error={v.shown('client')} hint="Only clients of this organization can be chosen.">
          {offer ? <Input readOnly {...bind('client')} /> : <NameSelect kind="client" lookups={lookups} current={editing ? placement.client : ''} {...bind('client')} />}
        </Field>
        <Field label="Recruiter *" error={v.shown('recruiter')} hint="Only recruiters of this organization can be chosen.">
          <NameSelect kind="recruiter" lookups={lookups} current={editing ? placement.recruiter : ''} {...bind('recruiter')} />
        </Field>
        <Field label="Employment status *" error={v.shown('employment_status')}><Select placeholder="Select" options={withCurrent(EMPLOYMENT, original && original.employment_status)} {...bind('employment_status')} /></Field>
        <Field label="Payment terms *" error={v.shown('payment_terms')}><Select placeholder="Select" options={withCurrent(PAYMENT_TERMS, original && original.payment_terms)} {...bind('payment_terms')} /></Field>
        <Field label="Team *" error={v.shown('lead')}><Select placeholder="Select team" options={teamKeys.map((t) => ({ value: t, label: TEAM_LABELS[t] }))} {...bind('lead')} /></Field>
        <Field label="Location" error={v.shown('loc')}><Input maxLength={50} {...bind('loc')} /></Field>
        <Field label={editing ? 'Bill rate' : 'Bill rate *'} error={v.shown('bill_rate')} hint="Per hour"><Input type="number" step="0.01" min="0" {...bind('bill_rate')} /></Field>
        <Field label={editing ? 'Pay rate' : 'Pay rate *'} error={v.shown('pay_rate')} hint="Per hour"><Input type="number" step="0.01" min="0" {...bind('pay_rate')} /></Field>
        <Field label="Gross margin" error={v.shown('gross_margin')} hint={negative ? 'Pay rate is higher than bill rate, so the margin is negative.' : 'Calculated from bill and pay rate'}><Input {...bind('gross_margin')} /></Field>
        <Field label="Actual margin" error={v.shown('actual_margin')}><Input {...bind('actual_margin')} /></Field>
        <Field label="End date" error={v.shown('project_end')} hint="Setting an end date marks the placement Inactive"><Input type="date" min="2000-01-01" max={dayFromNow(0)} {...bind('project_end')} /></Field>
        <Field label="Projected end date" error={v.shown('projected_end_date')}><Input type="date" min="2000-01-01" {...bind('projected_end_date')} /></Field>
        <Field label="Source" error={v.shown('source')}><Input maxLength={50} {...bind('source')} /></Field>
        <Field label="Status"><Input value={form.active_inactive} readOnly /></Field>
      </div>
    </Drawer>
  );
}
